import type { MenuSection } from '@1js/acui-menu/lib/components/MenuDefinition';
import type { RibbonButtonProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon';
import type { IButtonStyles } from '@fluentui/react/lib/Button';
import type { IKeytipProps } from '@fluentui/react/lib/Keytip';
import {
    AppButton,
    constructKeytip,
    getKeytipString,
    getRibbonIconColor,
} from 'owa-command-ribbon';
import BlockRegular from 'owa-fluent-icons-svg/lib/icons/BlockRegular';
import loc from 'owa-localize';
import showScheduleDatePickerDialog from 'owa-mail-datepicker/lib/components/ScheduleDatePicker';
import {
    scheduleChooseDate,
    scheduleLaterToday,
    scheduleNextWeek,
    scheduleThisEvening,
    scheduleThisWeekend,
    scheduleTomorrow,
    unsnooze,
} from 'owa-mail-datepicker/lib/utils/createScheduleSubMenu.locstring.json';
import { datePickerMinTime } from 'owa-mail-datepicker/lib/utils/createSnoozeSubMenu';
import getDateOptions from 'owa-mail-datepicker/lib/utils/getDateOptions';
import { listViewStore, TableQueryType } from 'owa-mail-list-store';
import { observableNow } from 'owa-observable-datetime';
import { getStringRibbonId, MailRibbonControlId, MailRibbonTabId, RibbonId } from 'owa-ribbon-ids';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { onSnoozeClicked } from '../../util/commandActions';

const snoozeContextMenuWidth = 246;

const getSnoozeContextMenuStyle: IButtonStyles = {
    root: {
        width: snoozeContextMenuWidth,
    },
};

const getSnoozeButtonId = (id: string | number): string => {
    if (typeof id == 'string') {
        return id;
    }

    return getStringRibbonId(id);
};

const getSnoozeIconColor = (id: string | number): string | undefined => {
    if (typeof id == 'string') {
        return undefined;
    }

    return getRibbonIconColor(id);
};

const createSnoozingButton = (
    id: string | number,
    label: string,
    onExecute: () => void,
    keytipProps?: IKeytipProps,
    icon?: string,
    overrides?
): RibbonButtonProps => {
    // Create ariaLabel: label + time
    const secondaryText = overrides?.secondaryText;
    const ariaLabel = secondaryText ? label + ', ' + secondaryText : undefined;

    return {
        type: AppButton,
        id: getSnoozeButtonId(id),
        label,
        icon, // Used for buttons within a Flyout menu
        iconSet: { default: icon }, // Used for standard buttons on the ribbon (not in a flyout menu)
        iconColor: getSnoozeIconColor(id),
        styles: getSnoozeContextMenuStyle,
        onExecute,
        keytipProps,
        fullDescriptionLabel: ariaLabel,
        ...overrides,
    };
};

/**
 * This function returns the drop-down content of the Snooze button in the Ribbon.
 * There are two sections here:
 *      1) Snooze (gives user options of times and option to pick their own time)
 *      2) Unsnooze (only shows if clicked item is already scheduled for later)
 */
export default function getSnoozeSections(): MenuSection[] {
    let snoozeSections: MenuSection[] = [];

    // Generate pre-populated snooze time options
    let snoozeSubsection: RibbonButtonProps[] = [];
    const snoozeDateOptions = getDateOptions();

    snoozeDateOptions.forEach(dateOption => {
        let id: RibbonId | string;

        switch (dateOption.name) {
            case scheduleLaterToday:
                id = MailRibbonControlId.SnoozeLaterToday;
                break;
            case scheduleThisEvening:
                id = MailRibbonControlId.SnoozeThisEvening;
                break;
            case scheduleTomorrow:
                id = MailRibbonControlId.SnoozeTomorrow;
                break;
            case scheduleThisWeekend:
                id = MailRibbonControlId.SnoozeWeekend;
                break;
            case scheduleNextWeek:
                id = MailRibbonControlId.SnoozeNextWeek;
                break;
            default:
                id = 'SnoozeOption_' + dateOption.name;
                break;
        }

        const { name, secondaryText, date } = dateOption;

        snoozeSubsection.push(
            createSnoozingButton(
                id,
                name /* label */,
                () => {
                    onSnoozeClicked(date);
                },
                typeof id !== 'string'
                    ? constructKeytip(
                          [
                              getKeytipString(MailRibbonTabId.Tab_Home),
                              getKeytipString(MailRibbonControlId.Snooze),
                          ],
                          getKeytipString(id)
                      )
                    : undefined,
                undefined /* icon */,
                {
                    secondaryText: secondaryText,
                }
            )
        );
    });

    let unsnoozeSubsection: RibbonButtonProps[] = [];

    // Insert "Choose Date" option

    unsnoozeSubsection.push(
        createSnoozingButton(
            MailRibbonControlId.SnoozeChooseDate,
            loc(scheduleChooseDate),
            () => {
                showScheduleDatePickerDialog({
                    minTime: datePickerMinTime(observableNow()),
                    onScheduleItemClicked: date => onSnoozeClicked(date),
                });
            },
            constructKeytip(
                [
                    getKeytipString(MailRibbonTabId.Tab_Home),
                    getKeytipString(MailRibbonControlId.Snooze),
                ],
                getKeytipString(MailRibbonControlId.SnoozeChooseDate)
            )
        )
    );

    snoozeSections.push({ controls: snoozeSubsection });
    snoozeSections.push({ controls: unsnoozeSubsection });

    // If item is already scheduled, add in an option to unschedule.
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    if (
        tableView.tableQuery.type == TableQueryType.Folder &&
        folderIdToName(tableView.tableQuery.folderId) == 'scheduled'
    ) {
        snoozeSections.push({
            controls: [
                createSnoozingButton(
                    MailRibbonControlId.Unsnooze /* id */,
                    loc(unsnooze) /* label */,
                    () => {
                        onSnoozeClicked(undefined /* shouldUnschedule */);
                    },
                    constructKeytip(
                        [
                            getKeytipString(MailRibbonTabId.Tab_Home),
                            getKeytipString(MailRibbonControlId.Snooze),
                        ],
                        getKeytipString(MailRibbonControlId.Unsnooze)
                    ),
                    BlockRegular
                ),
            ],
        });
    }

    return snoozeSections;
}
